import { EventType } from './EventType';
/**
 * Airship Push.
 */
export class AirshipPush {
    constructor(plugin) {
        this.plugin = plugin;
        this.iOS = new AirshipPushIOS(plugin);
        this.android = new AirshipPushAndroid(plugin);
    }
    /**
     * Enables/disables notifications on Airship.
     *
     * When enabled, it will cause the user to be prompted for
     * the permission on platforms that support it.
     * To get the result of the prompt, use `enableUserNotifications`.
     * @param enabled true to enable, false to disable
     * @returns A promise.
     */
    setUserNotificationsEnabled(enabled) {
        return this.plugin.perform('push#setUserNotificationsEnabled', enabled);
    }
    /**
     * Checks if user notifications are enabled or not on Airship.
     * @returns A promise with the result.
     */
    isUserNotificationsEnabled() {
        return this.plugin.perform('push#isUserNotificationsEnabled');
    }
    /**
     * Enables user notifications.
     * @param options Optional options.
     * @returns A promise with the permission result.
     */
    enableUserNotifications(options) {
        return this.plugin.perform('push#enableUserNotifications', options);
    }
    /**
     * Gets the notification status.
     * @returns A promise with the result.
     */
    getNotificationStatus() {
        return this.plugin.perform('push#getNotificationStatus');
    }
    /**
     * Gets the registration token if generated.
     * Use the event EventType.PushTokenReceived to be notified
     * when available.
     * @returns A promise with the result.
     */
    getPushToken() {
        return this.plugin.perform('push#getPushToken');
    }
    /**
     * Gets the list of active notifications.
     *
     * On Android, this list only includes notifications
     * sent through Airship.
     * @returns A promise with the result.
     */
    getActiveNotifications() {
        return this.plugin.perform('push#getActiveNotifications');
    }
    /**
     * Clears all notifications for the app.
     * @returns A promise with the result.
     */
    clearNotifications() {
        return this.plugin.perform('push#clearNotifications');
    }
    /**
     * Clears a specific notification.
     *
     * On Android, you can use this method to clear
     * notifications outside of Airship, The identifier is in
     * the format of <tag>:<id>.
     * @param identifier The identifier.
     * @returns A promise with the result.
     */
    clearNotification(identifier) {
        return this.plugin.perform('push#clearNotification', identifier);
    }
    /**
     * Adds a notification response event listener.
     */
    onNotificationResponse(listener) {
        return this.plugin.addListener(EventType.NotificationResponse, listener);
    }
    /**
     * Adds a push received event listener.
     */
    onPushReceived(listener) {
        return this.plugin.addListener(EventType.PushReceived, listener);
    }
    /**
     * Adds a notification status changed event listener.
     */
    onNotificationStatusChanged(listener) {
        return this.plugin.addListener(EventType.PushNotificationStatusChangedStatus, listener);
    }
    /**
     * Adds a notification status changed event listener.
     */
    onPushTokenReceived(listener) {
        return this.plugin.addListener(EventType.PushTokenReceived, listener);
    }
}
/**
 * iOS Push.
 */
export class AirshipPushIOS {
    constructor(plugin) {
        this.plugin = plugin;
    }
    /**
     * Sets the foreground presentation options.
     * @param options The foreground options.
     * @returns A promise.
     */
    setForegroundPresentationOptions(options) {
        return this.plugin.perform('push#ios#setForegroundPresentationOptions', options);
    }
    /**
     * Sets the notification options.
     * @param options The notification options.
     * @returns A promise.
     */
    setNotificationOptions(options) {
        return this.plugin.perform('push#ios#setNotificationOptions', options);
    }
    /**
     * Checks if autobadge is enabled.
     * @returns A promise with the result.
     */
    isAutobadgeEnabled() {
        return this.plugin.perform('push#ios#isAutobadgeEnabled');
    }
    /**
     * Enables/disables autobadge.
     * @param enabled true to enable, false to disable.
     * @returns A promise.
     */
    setAutobadgeEnabled(enabled) {
        return this.plugin.perform('push#ios#setAutobadgeEnabled', enabled);
    }
    /**
     * Set the badge number.
     * @param badge The badge number.
     * @returns A promise.
     */
    setBadgeNumber(badge) {
        return this.plugin.perform('push#ios#setBadgeNumber', badge);
    }
    /**
     * Gets the badge number.
     * @returns A promise with the result.
     */
    getBadgeNumber() {
        return this.plugin.perform('push#ios#getBadgeNumber');
    }
    /**
     * Enables/disables quiet time.
     *
     * @param enabled true to enable, false to disable
     * @returns A promise with the result.
     */
    setQuietTimeEnabled(enabled) {
        return this.plugin.perform('push#ios#setQuietTimeEnabled', enabled);
    }
    /**
     * Checks if quiet time is enabled or not.
     * @returns A promise with the result.
     */
    isQuietTimeEnabled() {
        return this.plugin.perform('push#ios#isQuietTimeEnabled');
    }
    /**
     * Sets quiet time.
     *
     * @param quietTime The quiet time.
     * @returns A promise with the result.
     */
    setQuietTime(quietTime) {
        return this.plugin.perform('push#ios#setQuietTime', quietTime);
    }
    /**
     * Gets the quiet time settings.
     *
     * @returns A promise with the result.
     */
    getQuietTime() {
        return this.plugin.perform('push#ios#getQuietTime');
    }
    /**
     * Adds a authorized settings changed event listener.
     */
    onAuthorizedSettingsChanged(listener) {
        return this.plugin.addListener(EventType.IOSAuthorizedNotificationSettingsChanged, listener);
    }
}
/**
 * Android Push.
 */
export class AirshipPushAndroid {
    constructor(plugin) {
        this.plugin = plugin;
    }
    /**
     * Checks if a notification category/channel is enabled.
     * @param channel The channel name.
     * @returns A promise with the result.
     */
    isNotificationChannelEnabled(channel) {
        return this.plugin.perform('push#android#isNotificationChannelEnabled', channel);
    }
    /**
     * Sets the notification config.
     * @param config The notification config.
     * @returns A promise with the result.
     */
    setNotificationConfig(config) {
        return this.plugin.perform('push#android#setNotificationConfig', config);
    }
    /**
     * Enables/disables showing notifications in the foreground.
     * @param enabled true to enable, false to disable.
     * @returns A promise with the result.
     */
    setForegroundNotificationsEnabled(enabled) {
        return this.plugin.perform('push#android#setForegroundNotificationsEnabled', enabled);
    }
}
//# sourceMappingURL=AirshipPush.js.map